import { Colors } from "../../config/DesignConstants";
import { colorLerp } from "../../core/util/ColorUtils";
import { lerp } from "../../core/util/MathUtil";
import { createRegularPolygon } from "../../core/util/PolygonUtils";
import { V, V2d } from "../../core/Vector";
import { VectorFilter } from "../graphics/filters/VectorFilter";
import VectorSprite from "../graphics/VectorSprite";
import { isShieldDamager } from "../interfaces/ShieldDamager";
import { LevelEntity } from "../LevelEntity";
import { SHIELD_COLOR } from "./Shield";

// TODO: Import this directly. Can't right now due to circular dependency issues
const SHIELD_RADIUS = 40;

// Pulse visual constants
const SIDES = 32;
const PULSE_SIZE = 40; // Amount to expand radius
const MIN_RADIUS = SHIELD_RADIUS;
const MAX_RADIUS = SHIELD_RADIUS + PULSE_SIZE;
const DURATION = 0.15; // seconds
const INITIAL_STROKE_WIDTH = 1;
const FINAL_STROKE_WIDTH = 1;
const MAX_IMPULSE = 20_000;
const INITIAL_ALPHA = 0.5;

/**
 * ShieldPulse entity - creates expanding pulse visual and applies impulse to nearby entities
 */
export default class ShieldPulse extends LevelEntity {
  private pulseRenderer: VectorSprite;

  constructor(impactPosition: V2d) {
    super();
    this.tags = ["shieldPulse", "effect"];

    // Create initial pulse ring
    const initialPoints = createRegularPolygon(SIDES, MIN_RADIUS);
    this.pulseRenderer = VectorSprite.fromPolygon(
      initialPoints,
      Colors.WHITE,
      INITIAL_STROKE_WIDTH,
    );
    this.pulseRenderer.setAlpha(INITIAL_ALPHA);
    this.pulseRenderer.addFilter(
      new VectorFilter(
        (segments, context) =>
          segments.map((segment) => {
            const t = context.t ?? 0; // Use context t or default to 0
            const scale = lerp(1, MAX_RADIUS / MIN_RADIUS, t ** 0.5);
            return {
              ...segment,
              color: colorLerp(Colors.WHITE, SHIELD_COLOR, t),
              alpha: lerp(INITIAL_ALPHA, 0, t ** 2),
              thickness: lerp(INITIAL_STROKE_WIDTH, FINAL_STROKE_WIDTH, t ** 2),
              start: segment.start.mul(scale),
              end: segment.start.mul(scale),
            };
          }),
        DURATION,
      ),
    );
    this.pulseRenderer.setPosition(impactPosition);
    this.addChild(this.pulseRenderer);
  }

  async onAdd(): Promise<void> {
    // Apply impulse effect immediately when added
    this.applyImpulseEffect();
    await this.waitRender(DURATION);
    this.destroy();
  }

  private applyImpulseEffect(): void {
    if (!this.game) return;

    // Get entities that can be pushed - filter entities with bodies for shield damagers
    const pushableEntities = Array.from(this.game.entities.withBody).filter(
      isShieldDamager,
    );

    const pulsePosition = V(0, 0); // shield position

    for (const entity of pushableEntities) {
      // Get entity position from physics body (no sketchy casting)
      const entityPosition = V(entity.body.position);

      // Calculate distance using Vector math
      // Note: This is distance to entity center, not edge. For more precision,
      // we could subtract shield radius (~40px) from impact distance.
      const distance = entityPosition.sub(pulsePosition).magnitude;

      // Only affect entities within impulse radius
      if (distance <= MAX_RADIUS && distance > 0) {
        // Calculate impulse direction (away from pulse center)
        const impulseDirection = entityPosition.sub(pulsePosition).normalize();

        // Calculate impulse strength (stronger when closer)
        const impulseStrength = MAX_IMPULSE * (1 - distance / MAX_RADIUS);

        // Apply the impulse force
        const impulseForce = impulseDirection.mul(impulseStrength);
        entity.body.applyImpulse(impulseForce);
      }
    }
  }
}
